import { useEffect } from "react";
import { Toaster } from "react-hot-toast";
import { connect } from "react-redux";

import "@cyntler/react-doc-viewer/dist/index.css";

import { InternalErrorPage } from "@/SentryInternalErrorPage";
import { RenderRoutes } from "@/routes/routes";
import * as Sentry from "@sentry/react";

import {
  resetAuthError,
  resetAuthSuccess,
  resetAuthFetching,
} from "@/redux/auth";
import { useAppSelector } from "@/redux/hook";
import { resetInvoicingFetching } from "@/redux/invoicing";
import { resetListingFetching } from "@/redux/listing";
import { getMaintenanceDataAction } from "@/redux/maintenance";
import type { GetMaintenanceTypes } from "@/redux/maintenance/maintenanceTypes";
import { resetOpenItemsFetching } from "@/redux/openItems";
import type { RootState } from "@/redux/rootReducers";
import { resetServicesFetching } from "@/redux/services";

import GlobalNetworkErrorModal from "@/components/commons/GlobalNetworkErrorModal";
import GlobalSessionExpiredModal from "@/components/commons/GlobalSessionExpiredModal";

import { subscribeAuthMessages } from "@/utils/authBroadcast";
import { clearInterceptor } from "@/utils/axiosInterceptor";

import "@/App.css";

import FloatingSupportButton from "./components/FloatingSupportIcon/FloatingSupportButton";

type AppProps = {
  token: string | null;
  dispatchResetAuthError: () => void;
  dispatchResetAuthFetching: () => void;
  dispatchResetServicesFetching: () => void;
  dispatchResetInvoicingFetching: () => void;
  dispatchResetAuthSuccess: () => void;
  dispatchResetListingFetching: () => void;
  dispatchResetOpenItemsFetching: () => void;
  isMaintenance?: number;
  maintenanceData?: GetMaintenanceTypes;
  dispatchGetMaintenanceData: () => void;
};

function App({
  token,
  dispatchResetAuthError,
  dispatchResetAuthFetching,
  dispatchResetInvoicingFetching,
  dispatchResetServicesFetching,
  dispatchResetOpenItemsFetching,
  dispatchResetListingFetching,
  dispatchGetMaintenanceData,
  isMaintenance,
  maintenanceData,
}: AppProps) {
  const userDetails = useAppSelector((state) => state.auth.loginData);
  // commented for future use
  // const showSupportIcon =
  //   !location.pathname.includes("/support") &&
  //   !location.pathname.includes("/login");

  const resetFetching = () => {
    dispatchResetAuthFetching();
    dispatchResetInvoicingFetching();
    dispatchResetServicesFetching();
    dispatchResetOpenItemsFetching();
    dispatchResetListingFetching();
  };

  // commented for future use
  // const handleSupport = () => {
  //   navigate("/support");
  // };

  useEffect(() => {
    dispatchResetAuthError();
    window.addEventListener("beforeunload", resetFetching);

    return () => {
      clearInterceptor();
      window.removeEventListener("beforeunload", resetFetching);
    };
  }, []);

  // Keep auth state in sync across tabs: when the user logs in or out in one
  // tab, reload the others so redux-persist rehydrates and routing corrects.
  // The small delay lets the originating tab flush the new auth state to
  // localStorage before this tab rehydrates from it.
  useEffect(() => {
    const unsubscribe = subscribeAuthMessages(() => {
      setTimeout(() => window.location.reload(), 400);
    });
    return unsubscribe;
  }, []);

  // setting sentry user
  useEffect(() => {
    if (
      Sentry.isEnabled() &&
      (userDetails?.email ||
        userDetails?.first_name ||
        userDetails?.last_name ||
        userDetails?.user_id)
    ) {
      const username =
        (userDetails?.first_name ?? "") + (userDetails?.last_name ?? "");
      const email = userDetails.email ?? "";
      const id = userDetails?.user_id ?? "";
      Sentry.setUser({
        email,
        username,
        id,
      });
    }
    return () => {
      Sentry.setUser(null);
    };
  }, [
    userDetails?.email,
    userDetails?.first_name,
    userDetails?.last_name,
    userDetails?.user_id,
  ]);

  useEffect(() => {
    dispatchGetMaintenanceData();
  }, []);

  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => <InternalErrorPage error={error} />}
    >
      <div className="main">
        {/* {
        isMaintenance === 1 ? <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#ede6fc',
          height: '50px',
          width: '100%',
          color: '#744bd1',
          fontWeight: 'bolder'
        }}
        >
          <span>Upcoming maintenance is scheduled on Saturday June 15th at 3:30PM (Your time) and Finac will be unavailable for 2 hour. Be sure to save your work.</span>
        </div> : null
      } */}
        <RenderRoutes token={token} />
        <GlobalSessionExpiredModal />
        <GlobalNetworkErrorModal />
        <Toaster />

        <FloatingSupportButton />
        {/* commented for future use */}
        {/* {showSupportIcon ? (
        <OverlayTrigger
          placement="left"
          delay={{ show: 250, hide: 250 }}
          overlay={<Tooltip id="support">FINAC Support</Tooltip>}
        >
          <div
            onClick={handleSupport}
            className="support-icon"
          >
            <img
              src={supportWhiteIcon}
              alt="Support"
            />
          </div>
        </OverlayTrigger>
      ) : null} */}
      </div>
    </Sentry.ErrorBoundary>
  );
}

const mapStateToProps = (state: RootState) => ({
  token: state.auth.token,
  error: state.auth.error,
  isMaintenance: state.maintenance.isMaintenance,
  maintenanceData: state.maintenance.maintenanceData,
});

const matchDispatchToProps = (dispatch: any) => ({
  dispatchResetAuthError: () => dispatch(resetAuthError()),
  dispatchResetAuthSuccess: () => dispatch(resetAuthSuccess()),
  dispatchResetAuthFetching: () => dispatch(resetAuthFetching()),
  dispatchResetServicesFetching: () => dispatch(resetServicesFetching()),
  dispatchResetInvoicingFetching: () => dispatch(resetInvoicingFetching()),
  dispatchResetListingFetching: () => dispatch(resetListingFetching()),
  dispatchResetOpenItemsFetching: () => dispatch(resetOpenItemsFetching()),
  dispatchGetMaintenanceData: () => dispatch(getMaintenanceDataAction()),
});

export default connect(mapStateToProps, matchDispatchToProps)(App);
